import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { zodResolver } from "@hookform/resolvers/zod";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DefaultStoreProjectAssigneeUpdate } from "@/types/defaultStoreProjectAssignee";
import PageHeader from "@/components/PageHeader";
import { useDefaultStoreProjectAssigneeBreadcrumbs } from "@/modules/defaultStoreProjectAssignees/hooks/useDefaultStoreProjectAssigneeBreadcrumbs";
import { defaultStoreProjectAssigneeUpdate } from "@/schemas/defaultStoreProjectAssignee";
import { useCallback, useEffect, useMemo } from "react";
import Form from "@/components/Form";
import PageContent from "@/components/PageContent";
import SaveButton from "@/components/SaveButton";
import { isRequired } from "@/lib/form";
import CheckboxField from "@/components/CheckboxField";
import UserSelect from "@/components/UserSelect";
import Field from "@/components/Field";
import { useDefaultStoreProjectAssignee } from "@/modules/defaultStoreProjectAssignees/hooks/useDefaultStoreProjectAssignee";
import { useUpdateDefaultStoreProjectAssignee } from "@/modules/defaultStoreProjectAssignees/hooks/useUpdateDefaultStoreProjectAssignee";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import Spinner from "@/components/Spinner";
import ProjectTypeSelect from "@/components/ProjectTypeSelect";
import StoreSelect from "@/components/StoreSelect";
import { DeleteButton } from "@/components/DeleteButton";
import { useDeleteDefaultStoreProjectAssignee } from "@/modules/defaultStoreProjectAssignees/hooks/useDeleteDefaultStoreProjectAssignee";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CheckedState } from "@radix-ui/react-checkbox";
import { SystemRoleCodesType } from "@/types/user";
import { SUPPORTED_SYSTEM_ROLE_CODES } from "../constants";
import { isEmptyArray } from "@/lib/utils/isEmptyArray";

const defaultValues: DefaultValues<DefaultStoreProjectAssigneeUpdate> = {
  store_id: "",
  project_type_id: "",
  is_primary: false,
  user_id: "",
  selectedSystemRoleCodes: SUPPORTED_SYSTEM_ROLE_CODES,
};

export default function DefaultStoreProjectAssigneesEditor() {
  const { t } = useTranslation();
  const deleteMutation = useDeleteDefaultStoreProjectAssignee();
  const { data, isLoading } = useDefaultStoreProjectAssignee(
    !deleteMutation.isPending && !deleteMutation.isSuccess,
  );
  const mutation = useUpdateDefaultStoreProjectAssignee();
  const { can_update, can_delete } = useModulePermissions();
  const breadcrumbItems = useDefaultStoreProjectAssigneeBreadcrumbs([
    {
      label: data?.user_name ?? "...",
    },
  ]);
  const isDisabled =
    deleteMutation.isPending || mutation.isPending || isLoading;

  const form = useForm<DefaultStoreProjectAssigneeUpdate>({
    resolver: zodResolver(defaultStoreProjectAssigneeUpdate),
    defaultValues,
    disabled: isDisabled,
  });

  const selectedSystemRoleCodes = form.watch("selectedSystemRoleCodes");

  const supportedRoles = useMemo(
    () =>
      SUPPORTED_SYSTEM_ROLE_CODES.map((systemRoleCode) => ({
        value: systemRoleCode,
        label: t(`userRole.${systemRoleCode}`),
      })),
    [t],
  );

  const handleRoleChange = useCallback(
    (role: SystemRoleCodesType, checked: CheckedState) => {
      const current = form.getValues(
        "selectedSystemRoleCodes",
      ) as SystemRoleCodesType[];
      let updated: SystemRoleCodesType[];
      if (checked) {
        updated = current.includes(role) ? current : [...current, role];
      } else {
        updated = current.filter((r) => r !== role);
      }
      form.setValue("selectedSystemRoleCodes", updated, {
        shouldValidate: false,
      });
    },
    [form],
  );

  useEffect(() => {
    if (!data) return;

    let selectedRoles: SystemRoleCodesType[] = SUPPORTED_SYSTEM_ROLE_CODES;
    if (data.user_role_code) {
      const currentRole = data.user_role_code as SystemRoleCodesType;
      if (SUPPORTED_SYSTEM_ROLE_CODES.includes(currentRole)) {
        selectedRoles = [currentRole];
      }
    }

    form.reset({
      ...data,
      selectedSystemRoleCodes: selectedRoles,
    });
  }, [data?.id, form]);

  useFormUnsavedChangesWarning(form);

  const handleSubmit = useCallback(
    async (formData: DefaultStoreProjectAssigneeUpdate) => {
      const { selectedSystemRoleCodes, ...data } = formData;
      await mutation.mutateAsync(data);
    },
    [mutation],
  );

  const handleDelete = useCallback(async () => {
    if (!data?.id) return;
    await deleteMutation.mutateAsync(data.id);
  }, [deleteMutation, data]);

  if (isLoading) {
    return (
      <Spinner
        label={t("defaultStoreProjectAssignees.loading")}
        className="w-full h-full"
      />
    );
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <Form form={form} handleSubmit={handleSubmit}>
        <PageContent
          title={t("defaultStoreProjectAssignees.title.edit")}
          footer={
            <div className="flex justify-end gap-2">
              <DeleteButton
                onClick={handleDelete}
                disabled={isDisabled || !can_delete}
              />
              <SaveButton disabled={isDisabled || !can_update} />
            </div>
          }
        >
          <div className="flex flex-row gap-4 mt-6">
            <div className="text-sm">
              {t("defaultStoreProjectAssignees.title.role")}
            </div>
            {supportedRoles.map((role) => (
              <Label
                className="flex items-center gap-2 font-normal"
                key={role.value}
              >
                <Checkbox
                  value={role.value}
                  onCheckedChange={(checked) =>
                    handleRoleChange(role.value, checked)
                  }
                  checked={
                    selectedSystemRoleCodes?.includes(role.value) ?? false
                  }
                />
                {role.label}
              </Label>
            ))}
          </div>

          <div className="flex flex-row gap-4 my-6 items-center">
            <Field
              name="user_id"
              label={t("defaultStoreProjectAssignees.fields.user_name")}
              required={isRequired(
                defaultStoreProjectAssigneeUpdate.shape.user_id,
              )}
              render={({ field }) => (
                <UserSelect
                  {...field}
                  className="w-80"
                  defaultItem={{ id: data?.user_id, name: data?.user_name }}
                  systemRoleCodes={selectedSystemRoleCodes ?? []}
                  disabled={
                    field.disabled || isEmptyArray(selectedSystemRoleCodes)
                  }
                  showRole={true}
                />
              )}
            />
            <CheckboxField
              name="is_primary"
              label={t(`defaultStoreProjectAssignees.fields.is_primary`)}
              required={isRequired(
                defaultStoreProjectAssigneeUpdate.shape.is_primary,
              )}
              disabled={isDisabled}
            />
          </div>
          <div className="flex flex-row gap-4 my-6">
            <Field
              name="project_type_id"
              label={t("defaultStoreProjectAssignees.fields.project_type_name")}
              required={isRequired(
                defaultStoreProjectAssigneeUpdate.shape.project_type_id,
              )}
              render={({ field }) => (
                <ProjectTypeSelect
                  {...field}
                  className="w-80"
                  defaultItem={{
                    id: data?.project_type_id,
                    name: data?.project_type_name,
                  }}
                />
              )}
              className="w-80"
            />
            <Field
              name="store_id"
              label={t("defaultStoreProjectAssignees.fields.store_name")}
              required={isRequired(
                defaultStoreProjectAssigneeUpdate.shape.store_id,
              )}
              render={({ field }) => (
                <StoreSelect
                  {...field}
                  className="w-80"
                  defaultItem={{ id: data?.store_id, name: data?.store_name }}
                />
              )}
              className="w-80"
            />
          </div>
        </PageContent>
      </Form>
    </>
  );
}
